import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react/lib/agGridReact';
import { useStoreon } from 'storeon/react';
import { toast } from 'react-toastify';
import { GxSpinner } from '@garpix/garpix-web-components-react';
import { ColDef, LicenseManager } from 'ag-grid-enterprise';
import { DateObject } from 'react-multi-date-picker';
import classNames from 'classnames';
import { api } from '@/api';
import { reportsApiRes } from '@/api/interfaces/responses';
import { Events, State } from '@/interfaces';
import styles from './styles/index.module.scss';
import { AG_GRID_LOCALE_RU, defaultValuesColumnOrders, defaultValuesColumnPayments, defaultValuesColumnInvoices, REPORT_STATUS, STATISTICS_TAB } from './const';
import {customRowsStyle, defaultDateFilters, getFormattedNumber, startDateFilters} from './helpers';
import StatisticsHeader from './StatisticsHeader';

LicenseManager.setLicenseKey('LICENSE_HACK_BY_SUDHEER_[v26.1.1]__b1507244ab90657f098d0e85024b36d5');

const Statistics = () => {
  const [flagLoader, setFlagLoader] = useState<boolean>(false);
  const [rowData, setRowData] = useState<reportsApiRes.IGetStatisticsRes['data']>([]);
  const [tab, setTab] = useState<STATISTICS_TAB>(STATISTICS_TAB.ORDERS);
  const [defaultValuesColumn, setDefaultValuesColumn] = useState<ColDef[]>(defaultValuesColumnOrders);
  const toggleFlagSpinner = useCallback(() => setFlagLoader((prev) => !prev), []);
  const [defaultDate, setDefaultDate] = useState<DateObject[]>([]);
  const gridRef = useRef<AgGridReact>(null);
  const { dispatch, report } = useStoreon<State, Events>('report');

  const defaultColDef = useMemo(() => {
    return {
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      sortable: true,
      resizable: true,
      enablePivot: true,
      filterParams: {
        newRowsAction: 'keep',
        browserDatePicker: true
      },
      allowedAggFuncs: ['sum', 'min', 'max', 'avg', 'count'],
      valueFormatter: getFormattedNumber,
      headerClass: styles['table--header'],
      cellClass: styles['table--cell']
    };
  }, []);
  const columnTypes = useMemo(() => {
    return {
      numberColumn: { width: 130, filter: 'agNumberColumnFilter' },
      medalColumn: { width: 100, columnGroupShow: 'open', filter: false },
      nonEditableColumn: { editable: false },
      startDateColumn: {
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: startDateFilters
        },
      },
      defaultDateColumn: {
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: defaultDateFilters
        },
      },
    };
  }, []);

  const handleClickHeader = (name: STATISTICS_TAB) => {
    dispatch('presets/get', { report_name: name });
    setTab(name)
  }

  const handleClickDatePicker = async (date: DateObject[]) => {
    toggleFlagSpinner();
    await api.reports.createStatistics(tab, {
      start_date: new DateObject(date[0]).format('DD.MM.YYYY'),
      end_date:
      date.length === 1
      ? new DateObject(date[0]).format('DD.MM.YYYY') 
      : new DateObject(date[1]).format('DD.MM.YYYY'),
    })
  }

  const loadingOverlayComponentParams = useMemo(() => {
    return {
      loadingMessage: 'Идет загрузка...',
    };
  }, []);

  const reformDate = (date: string) => {
    const temp = date.split('.');
    return new DateObject(`${temp[2]}.${temp[1]}.${temp[0]}`);
  }

  useEffect(() => {
    switch (tab) {
      case 'orders':
        setDefaultValuesColumn(defaultValuesColumnOrders); break;
      case 'payments':
        setDefaultValuesColumn(defaultValuesColumnPayments); break;
      case 'invoices':
        setDefaultValuesColumn(defaultValuesColumnInvoices); break;
      default:
        setDefaultValuesColumn(defaultValuesColumnOrders); break;
    }

    setFlagLoader(true);
    api.reports.getStatistics(tab).then((resp) => {
      setFlagLoader(false);
      setRowData(resp.data);
      setDefaultDate([reformDate(resp.start_from_at), reformDate(resp.start_end_at)])
    }, () => {
      setFlagLoader(false);
      toast.error('Ошибка при получении отчета');
    })
  }, [tab])

  useEffect(() => {
    if (report[tab] === REPORT_STATUS.FORMING) {
      setFlagLoader(true);
    }

    if (report[tab] === REPORT_STATUS.READY) {
      setFlagLoader(true);
      api.reports.getStatistics(tab).then((resp) => {
        setFlagLoader(false);
        setRowData(resp.data);
        setDefaultDate([reformDate(resp.start_from_at), reformDate(resp.start_end_at)]);
        report[tab] = REPORT_STATUS.NOT_REQUESTED;
      }, () => {
        setFlagLoader(false);
        toast.error('Ошибка при получении отчета');
      })
    }
  }, [report])

  return (
    <>
      <StatisticsHeader
        tab={tab}
        setTab={handleClickHeader}
        handleClickDatePicker={handleClickDatePicker}
        defaultDate={defaultDate}
        gridRef={gridRef}
      />

      {flagLoader ? (
        <div className={styles['content--status']}>
          <GxSpinner style={{ fontSize: '5rem' }} />
        </div>
      ) : (
        <div className={classNames('ag-theme-alpine', styles['table'])}>
          <AgGridReact
            localeText={AG_GRID_LOCALE_RU}
            rowData={rowData}
            columnDefs={defaultValuesColumn}
            defaultColDef={defaultColDef}
            columnTypes={columnTypes}
            getRowStyle={customRowsStyle}
            sideBar
            ref={gridRef}
            cacheBlockSize={100}
            rowGroupPanelShow='onlyWhenGrouping'
            pivotPanelShow='always'
            suppressDragLeaveHidesColumns
            suppressMakeColumnVisibleAfterUnGroup
            loadingOverlayComponentParams={loadingOverlayComponentParams}
            autoGroupColumnDef={{
              pinned: 'left'
            }}
          />
        </div>
      )}
    </>
  )
};

export default Statistics;